import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import * as LDClient from 'launchdarkly-js-client-sdk';
import environment from '@lib/environment';
import { type FeatureFlagKeys, isValidFeatureFlagKey } from './featureFlagKeys';
import type IAppEvents from '@app/lib/IAppEvents';
import type IAppLogger from '@app/lib/IAppLogger';
import { AuthEvents, AuthUser } from '@modules/AuthModule/types';

/**
 * The key of the localStorage item that stores feature flag overrides.
 */
export const FEATURE_FLAG_OVERRIDES_LOCAL_STORAGE_KEY = 'featureFlagOverrides';

/**
 * The default value of a feature flag from LaunchDarkly if the flag is not available.
 * Strictly for internal use.
 */
const DEFAULT_LD_FLAG_VALUE = { key: 'default' };

type ValueResponse<T = any> =
  | { hasValue: true; value: T }
  | { hasValue: false; value: undefined };

type TrackingResponse<T = any> = ValueResponse<T> & {
  /**
   * Whether this call initialized tracking for the feature flag for the first time this session.
   */
  trackingStarted?: boolean;
};

interface RegisterFeatureFlagListenerResponse {
  /**
   * A handler to remove the listener, good for cleanup.
   */
  remove: () => void;
}

interface RegisterFeatureFlagListenerOptions {
  /**
   * Whether to emit the initial value of the feature flag to the listener when it is first registered.
   * If the flag has no value, the listener will not be called.
   * @default false
   */
  emitInitialValue?: boolean;
}

export class FeatureFlagModule extends AppModule {
  /**
   * A cache of feature flag overrides from localStorage.
   * This is kept in sync and used to determine which flag overrides changed
   * when the localStorage feature flag overrides change.
   */
  private readonly _featureFlagOverrides: Record<string, unknown> = {};

  /**
   * A map of feature flag keys to sets of listeners.
   * This is used to store the listeners that are called when a given (key) feature flag changes.
   */
  private readonly _featureFlagListeners: {
    [key in FeatureFlagKeys]?: Set<(value: ValueResponse<unknown>) => void>;
  } = {};

  /**
   * Feature flags that should be tracked but are not yet being tracked.
   * Often due to the LaunchDarkly client not being initialized at the time of the tracking request.
   */
  private readonly _pendingTrackedFeatureFlags: Set<FeatureFlagKeys> =
    new Set();

  /**
   * Indicates the feature flags that are being tracked from LaunchDarkly for the remainder of the session.
   * If a key is in this set it is either (1) cached already in {@link _ldFeatureFlagCache} or (2) will be via {@link initializePendingTrackedFeatureFlags}
   * when the LaunchDarkly client is initialized.
   */
  private readonly _trackedFeatureFlags: Set<FeatureFlagKeys> = new Set();

  /**
   * An internal cache of feature flag values from LaunchDarkly to avoid unnecessary calls to LaunchDarkly.
   * Only reflects realtime value of flags that have been requested at some point in the session via
   * {@link tryTrackingFeatureFlag}, via a call to {@link getFeatureFlagValue}, or via a call to {@link addFlagListener}
   */
  private readonly _ldFeatureFlagCache: {
    [key in FeatureFlagKeys]?: LDClient.LDFlagValue;
  } = {};

  /**
   * The underlying LaunchDarkly client.
   * Undefined until the LaunchDarkly client is initialized and fully ready to use.
   */
  private _ldClient?: LDClient.LDClient;

  override events = ['featureflags.initialized'];

  constructor(events: IAppEvents, logger: IAppLogger) {
    super(events, logger);

    // initialize storage handler to update the overrides cache when the localStorage feature flag overrides change
    window.addEventListener('storage', (ev: StorageEvent) => {
      if (ev.key !== FEATURE_FLAG_OVERRIDES_LOCAL_STORAGE_KEY) return;
      const newOverrides = this.getFeatureFlagOverridesFromLocalStorage();
      this.handleFeatureFlagOverridesChange(newOverrides);
    });

    // initialize the overrides cache and emit events for the initial overrides, good for tests that need to access
    // the overrides but the LaunchDarkly client is not initialized.
    this.handleFeatureFlagOverridesChange(
      this.getFeatureFlagOverridesFromLocalStorage(),
    );
  }

  override preload(): void {
    let ldClient: LDClient.LDClient | undefined;
    this.app.events.on(AuthEvents.SIGNIN, (user: AuthUser) => {
      const ldContext: LDClient.LDContext = {
        kind: 'org',
        key: user.org.id,
      };

      const _client = LDClient.initialize(
        environment.LAUNCH_DARKLY_CLIENT_ID,
        ldContext,
        {
          sendEventsOnlyForVariation: true, // do not send events for calls to allFlags()
        },
      );
      ldClient = _client;

      _client.on('initialized', async () => {
        await _client.waitUntilReady();
        this._ldClient = _client;

        // initialize tracked feature flags (must be after areFlagsReady = true)
        this.initializePendingTrackedFeatureFlags();

        appManager.events.emit('featureflags.initialized');
      });

      this.app.logger.info('User context is', ldContext);
    });

    this.app.events.on(AuthEvents.SIGNOUT, () => {
      ldClient?.close();
      this._ldClient = undefined;

      // mark all tracked feature flags as pending to track so they pick back up when the client is initialized again
      const trackedFlags = Array.from(this._trackedFeatureFlags);
      this._trackedFeatureFlags.clear();
      trackedFlags.forEach((featureFlag) => {
        this._pendingTrackedFeatureFlags.add(featureFlag);
      });

      // connection with LD is severed, but we can still use the cache, so we don't need to cleanup anything else
    });
  }

  /**
   * Indicates the underlying LaunchDarkly client is initialized and ready to use.
   * Once this is true, feature flags can be reliably queried. Before this is true,
   * feature flags may not be available or may return undefined unless overridden or otherwise cached.
   */
  public get areFlagsReady(): boolean {
    return !!this._ldClient;
  }

  /**
   * An alias for {@link getFeatureFlagValue} with a guaranteed boolean return value.
   * If used on a non-boolean feature flag, the value will be coerced to a boolean based on if it is truthy or falsy.
   * @param featureFlag - the feature flag to check
   * @returns true if the feature flag is enabled, false otherwise
   */
  public isEnabled(featureFlag: FeatureFlagKeys): boolean {
    this.assertValidFeatureFlagKey(featureFlag);

    const { hasValue, value } = this.getFeatureFlagValue(featureFlag);
    if (!hasValue) return false;
    return !!value;
  }

  /**
   * Gets the current value of a feature flag including overrides.
   * @param featureFlag - the feature flag to get the value of
   * @returns the value of the feature flag
   */
  public getFeatureFlagValue<T = any>(
    featureFlag: FeatureFlagKeys,
  ): ValueResponse<T> {
    this.assertValidFeatureFlagKey(featureFlag);

    const overrideResponse = this.getFeatureFlagOverride<T>(featureFlag);
    if (overrideResponse.hasValue) return overrideResponse;

    return this.getUnoverriddenFeatureFlagValue<T>(featureFlag);
  }

  /**
   * Adds a listener to a feature flag.
   * @param featureFlag - the feature flag to listen for
   * @param listener - the listener to call when the feature flag changes
   * @param options - the options for the listener
   * @returns a function to remove the listener
   */
  public addFlagListener<TValue = any>(
    featureFlag: FeatureFlagKeys,
    listener: (value: ValueResponse<TValue>) => void,
    options: RegisterFeatureFlagListenerOptions = {},
  ): RegisterFeatureFlagListenerResponse {
    const { emitInitialValue = false } = options;

    this.assertValidFeatureFlagKey(featureFlag);

    const listeners = this._featureFlagListeners[featureFlag];
    if (listeners) {
      listeners.add(listener as (v: ValueResponse<unknown>) => void);
    } else {
      this._featureFlagListeners[featureFlag] = new Set([
        listener as (v: ValueResponse<unknown>) => void,
      ]);
    }

    // getting the initial value will also try to begin tracking the flag
    const valueResponse = this.getFeatureFlagValue<TValue>(featureFlag);

    if (emitInitialValue) {
      listener(valueResponse);
    }

    return {
      remove: (): void => {
        const listeners = this._featureFlagListeners[featureFlag];
        if (listeners) {
          listeners.delete(listener as (v: unknown) => void);
        }
      },
    };
  }

  /**
   * Get all feature flags including overrides.
   * Useful for error logging context.
   */
  public getAllFlags(): Record<string, any> {
    return this.getFeatureFlagsWithOverrides();
  }

  /**
   * Sets a single override and persists it to localStorage.
   * @param featureFlag - the feature flag to store the override for
   * @param value - the value of the override
   * @remarks Using LocalStorage directly is not recommended as
   * by design, modifications to localStorage do not emit events in that tab
   * (causing changes not to be reflected in {@link FeatureFlagModule} for that tab) see
   * https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
   */
  public setFeatureFlagOverride<T = any>(
    featureFlag: FeatureFlagKeys,
    value: T | undefined,
  ): void {
    this.assertValidFeatureFlagKey(featureFlag);

    // save the new value to the internal overrides cache and emit a change event to listeners
    const newOverrides = this.handleFeatureFlagOverrideChange(
      featureFlag,
      value,
    );

    // mirror the new overrides to localStorage
    localStorage.setItem(
      FEATURE_FLAG_OVERRIDES_LOCAL_STORAGE_KEY,
      JSON.stringify(newOverrides),
    );

    this.app.logger?.debug?.(
      `Feature flag override set: ${featureFlag} = ${value}`,
    );
  }

  /**
   * Clears all feature flag overrides and persists the change to localStorage.
   * @remarks Using LocalStorage directly is not recommended as
   * by design, modifications to localStorage do not emit events in that tab
   * (causing changes not to be reflected in {@link FeatureFlagModule} for that tab) see
   * {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event}
   */
  public clearAllFeatureFlagOverrides(): void {
    localStorage.removeItem(FEATURE_FLAG_OVERRIDES_LOCAL_STORAGE_KEY);
    this.handleFeatureFlagOverridesChange({});
    this.app.logger?.debug?.('All feature flag overrides cleared');
  }

  // #region Private methods

  /**
   * Gets the value of a feature flag from the LaunchDarkly client. Does not check for overrides, cached values, or tracking status.
   * @param featureFlag - the feature flag to get the value of
   * @returns the value of the feature flag, or undefined if the feature flag is not available or the client is not ready
   */
  private getFlagValueFromLDClient<T = any>(
    featureFlag: FeatureFlagKeys,
  ): ValueResponse<T> {
    // if the client is not initialized or the flags are not ready, return undefined.
    // Else, LD will log an error/warning which may cause a infinite loop with the Kibana module.
    if (!this._ldClient) {
      return { hasValue: false, value: undefined };
    }

    const output = this._ldClient.variation(featureFlag, DEFAULT_LD_FLAG_VALUE);
    if (output === DEFAULT_LD_FLAG_VALUE)
      return { hasValue: false, value: undefined };

    return { hasValue: true, value: output as T };
  }

  /**
   * Gets all overrides from localStorage.
   * @returns all overrides as a record of key-value pairs, or an empty object if no overrides are found
   */
  private getFeatureFlagOverridesFromLocalStorage() {
    const overridesString = localStorage.getItem(
      FEATURE_FLAG_OVERRIDES_LOCAL_STORAGE_KEY,
    );

    if (overridesString) {
      try {
        const overrides = JSON.parse(overridesString) as Record<
          string,
          boolean
        >;
        return overrides;
      } catch (error) {
        this.app.logger.error('Error parsing feature flag overrides', error);
        return {};
      }
    }

    return {};
  }

  /**
   * A handler to call when the feature flag override for a *specific* feature flag changes.
   * Manages the internal overrides cache and emits new feature flag values to listeners when the override changes.
   * DOES NOT PERSIST THE OVERRIDE TO LOCAL STORAGE.
   * @param featureFlag - the feature flag that changed
   * @param newValue - the new value of the feature flag
   * @returns the updated internal overrides cache
   */
  private handleFeatureFlagOverrideChange<T = any>(
    featureFlag: FeatureFlagKeys,
    newValue: T,
  ): Record<string, unknown> {
    const oldValue = this._featureFlagOverrides[featureFlag];
    if (oldValue === newValue) return this._featureFlagOverrides;

    if (newValue === undefined) {
      delete this._featureFlagOverrides[featureFlag];
    } else {
      this._featureFlagOverrides[featureFlag] = newValue;
    }

    // get the value including any base value from LaunchDarkly, if the flag was cleared
    const valueResponse = this.getFeatureFlagValue(featureFlag);
    this.emitFeatureFlagChange(featureFlag, valueResponse);

    return this._featureFlagOverrides;
  }

  /**
   * A handler to call when the localStorage feature flag overrides in localStorage change.
   * Updates the internal overrides cache and emits new feature flag values to listeners
   * when the override for a specific feature flag changes.
   * Validates the feature flag keys are valid against {@link isValidFeatureFlagKey} and only processes valid keys.
   */
  private handleFeatureFlagOverridesChange(
    newOverrides: Record<string, unknown>,
  ) {
    const allKeys = Array.from(
      new Set([
        ...Object.keys(newOverrides),
        ...Object.keys(this._featureFlagOverrides),
      ]),
    ).filter(isValidFeatureFlagKey);

    allKeys.forEach((key) => {
      this.handleFeatureFlagOverrideChange(key, newOverrides[key]);
    });
  }

  /**
   * Tries to get a cached value for a feature flag.
   * @param featureFlag - the feature flag to get the cached value of
   * @returns an object with the value/undefined and a boolean indicating if the value was found in the cache
   */
  private tryGetCachedFeatureFlag<T = any>(
    featureFlag: FeatureFlagKeys,
  ): ValueResponse<T> {
    const isCached = featureFlag in this._ldFeatureFlagCache;

    if (isCached) {
      return {
        value: this._ldFeatureFlagCache[featureFlag] as T,
        hasValue: true,
      };
    }

    return { hasValue: false, value: undefined };
  }

  /**
   * Emits a feature flag change to all listeners.
   * @param featureFlag - the feature flag that changed
   * @param value - the new value of the feature flag
   */
  private emitFeatureFlagChange(
    featureFlag: FeatureFlagKeys,
    valueResponse: ValueResponse<unknown>,
  ) {
    const listeners = this._featureFlagListeners[featureFlag];
    if (listeners) {
      listeners.forEach((listener) => listener(valueResponse));
    }
  }

  /**
   * Begins tracking a feature flag's value from LaunchDarkly for the remainder of the session.
   * Keeps the cache updated with the latest value from LaunchDarkly. No-op if the flag is already tracked.
   * Emits to listeners if the flag's value changes.
   * @param featureFlag - the feature flag to track
   * @param softFail - whether to fail silently if the LaunchDarkly client is not initialized. If true and the client is not initialized,
   *                   the flag will not be tracked and the cache will not be updated. If false and the client is not initialized,
   *                   an error will be thrown and the flag will not be tracked.
   * @returns a `TrackingResponse` with the latest flag value and a boolean indicating if tracking was started
   */
  private tryTrackingFeatureFlag<TValue = any>(
    featureFlag: FeatureFlagKeys,
    softFail = false,
  ): TrackingResponse<TValue> {
    if (this._trackedFeatureFlags.has(featureFlag)) {
      // (1) if uninitialized, all tracked flags will be retrieved when initialized, value will be undefined.
      // (2) if initialized but LD didn't have a value, the event listener will be called if the flag is ever available, value will be undefined.
      // (3) if we have a cached value and the flag is tracked, it should be the latest value from LaunchDarkly,
      //     worst case it's stale but better than nothing.
      return this.tryGetCachedFeatureFlag<TValue>(featureFlag);
    }

    // mark pending to track, just in case something goes wrong
    this._pendingTrackedFeatureFlags.add(featureFlag);

    if (!this._ldClient) {
      // if for some reason we have a stale cached value, return it, better than nothing
      if (softFail) return this.tryGetCachedFeatureFlag<TValue>(featureFlag);
      throw new Error('LaunchDarkly client not initialized');
    }

    this._ldClient.on(`change:${featureFlag}`, (newFlagValue) => {
      this._ldFeatureFlagCache[featureFlag] = newFlagValue;

      // Notify internal listeners of the change (only if no override is set)
      const { hasValue: hasOverride } =
        this.getFeatureFlagOverride(featureFlag);

      if (!hasOverride) {
        this.emitFeatureFlagChange(featureFlag, {
          hasValue: true, // if LD is emitting a value, then it exists
          value: newFlagValue,
        });
      }
    });

    // mark as tracked and remove from pending to track
    this._trackedFeatureFlags.add(featureFlag);
    this._pendingTrackedFeatureFlags.delete(featureFlag);

    const response = this.getFlagValueFromLDClient(featureFlag);
    if (response.hasValue) {
      this._ldFeatureFlagCache[featureFlag] = response.value;
    }

    // if LD client is not initialized but the listener is registered, when the value is available, the change listener will be called.
    // This is why we return trackingStarted: true even if the `response.hasValue` is false.
    return { ...response, trackingStarted: true };
  }

  /**
   * A handler to call after the LaunchDarkly client is initialized to track all feature flags
   * that were added to the _trackedFeatureFlags set prior to the client being initialized.
   * Emits the initial value to listeners if the flag has a value.
   */
  private initializePendingTrackedFeatureFlags() {
    const toTrack = Array.from(this._pendingTrackedFeatureFlags);

    toTrack.forEach((featureFlag) => {
      // the handler will mark the flag as tracked and remove from pending to track if successful
      const { trackingStarted, value, hasValue } = this.tryTrackingFeatureFlag(
        featureFlag,
        false,
      );

      if (trackingStarted && hasValue) {
        // emit the initial value to listeners, only if the flag has a value
        this.emitFeatureFlagChange(featureFlag, { hasValue, value });
      }
    });
  }

  /**
   * Begins tracking a feature flag (in cache) for the remainder of the session and returns the current value of the feature flag.
   * If called a second time, refreshes the cached value from LaunchDarkly, continues to track the flag for the remainder of the session.
   * @param featureFlag - the feature flag to track
   * @returns the current value of the feature flag
   */
  private getUnoverriddenFeatureFlagValue<TValue = any>(
    featureFlag: FeatureFlagKeys,
  ): ValueResponse<TValue> {
    // begin tracking, if the flag is not already tracked, and return the current value from the cache or LaunchDarkly
    const { value, hasValue } = this.tryTrackingFeatureFlag(featureFlag, true);

    if (hasValue) return { hasValue, value };
    return { hasValue: false, value: undefined };
  }

  /**
   * Gets a single override from localStorage.
   * @param key - the key of the override to get
   * @returns the override value, or undefined if no override is found
   */
  private getFeatureFlagOverride<T>(key: string): ValueResponse<T> {
    const hasOverride = key in this._featureFlagOverrides;
    if (!hasOverride) return { hasValue: false, value: undefined };
    return { hasValue: true, value: this._featureFlagOverrides[key] as T };
  }

  /**
   * Gets all feature flags including overrides.
   * @returns all feature flags as a record of key-value pairs, including overrides
   */
  private getFeatureFlagsWithOverrides() {
    return { ...this._ldClient?.allFlags(), ...this._featureFlagOverrides };
  }

  /**
   * Asserts that a feature flag key is valid against {@link isValidFeatureFlagKey}.
   * @param key - the feature flag key to assert
   * @throws an error if the feature flag key is invalid
   */
  private assertValidFeatureFlagKey(key: string): void {
    if (!isValidFeatureFlagKey(key)) {
      throw new Error(`Invalid feature flag key: ${key}`);
    }
  }

  // #endregion
}

export { FeatureFlagKeys } from './featureFlagKeys';
export {
  mockFeatureFlag,
  clearFeatureFlagMocks,
} from './mocks/mockFeatureFlag';
export { default as useFeatureFlag } from './hooks/useFeatureFlag/useFeatureFlag';
export { default as useFeatureFlags } from './hooks/useFeatureFlags';

export default new FeatureFlagModule(
  appManager.events,
  appManager.logger('FeatureFlag'),
);
